import { doc, getDoc, collection, query, where, getDocs, orderBy, limit, updateDoc, onSnapshot, deleteField } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from './firebase';
import type { AdaptiveSignalBatch, RecalibrationHistory, RecalibrationChange } from '../types/adaptive';

let _functions: ReturnType<typeof import('firebase/functions').getFunctions> | null = null;
async function getFunctionsInstance() {
  if (!_functions) {
    const { getFunctions } = await import('firebase/functions');
    const { app } = await import('./firebase');
    _functions = getFunctions(app);
  }
  return _functions;
}

export async function getLatestSignals(userId: string, pathId: string): Promise<AdaptiveSignalBatch | null> {
  const q = query(
    collection(db, 'adaptive', userId, 'signals'),
    where('pathId', '==', pathId),
    orderBy('processedAt', 'desc'),
    limit(1),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return snap.docs[0].data() as AdaptiveSignalBatch;
}

export async function getRecalibrationHistory(userId: string, maxEntries = 10): Promise<RecalibrationHistory[]> {
  const q = query(
    collection(db, 'adaptive', userId, 'history'),
    orderBy('timestamp', 'desc'),
    limit(maxEntries),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as RecalibrationHistory);
}

export async function hasPendingAdjustments(pathId: string): Promise<boolean> {
  const pathDoc = await getDoc(doc(db, 'learningPaths', pathId));
  if (!pathDoc.exists()) return false;
  return !!pathDoc.data().adjustmentMessage;
}

export async function getAdjustmentMessage(pathId: string): Promise<string | null> {
  const pathDoc = await getDoc(doc(db, 'learningPaths', pathId));
  if (!pathDoc.exists()) return null;
  return pathDoc.data().adjustmentMessage ?? null;
}

export async function dismissAdjustmentMessage(pathId: string): Promise<void> {
  await updateDoc(doc(db, 'learningPaths', pathId), {
    adjustmentMessage: deleteField(),
  });
}

export async function isDiagnosticRetakeSuggested(userId: string): Promise<boolean> {
  const userDoc = await getDoc(doc(db, 'users', userId));
  if (!userDoc.exists()) return false;
  return !!userDoc.data().diagnosticRetakeSuggested;
}

export async function dismissDiagnosticRetake(userId: string): Promise<void> {
  await updateDoc(doc(db, 'users', userId), {
    diagnosticRetakeSuggested: false,
    diagnosticRetakeDismissedAt: new Date().toISOString(),
  });
}

export function onProfileRecalibrated(
  userId: string,
  callback: (changes: RecalibrationChange[]) => void,
): () => void {
  return onSnapshot(doc(db, 'cogniprints', userId), (snapshot) => {
    if (!snapshot.exists()) return;
    // Check if there's a recent adaptation event we haven't seen
    const metadata = snapshot.metadata;
    if (metadata.hasPendingWrites) return; // Local write, not from server

    // The adaptation log on the profile indicates changes
    // The caller can check profile version to detect recalibration
    // We pass an empty array as a signal that a change occurred
    // The actual changes are in the adaptation log on the learning path
    callback([]);
  });
}

export function onPathAdjusted(
  pathId: string,
  callback: (message: string | null) => void,
): () => void {
  return onSnapshot(doc(db, 'learningPaths', pathId), (snapshot) => {
    if (!snapshot.exists()) return;
    const data = snapshot.data();
    callback(data.adjustmentMessage ?? null);
  });
}

export async function triggerContentRegeneration(pathId: string, nodeId: string): Promise<{ regenerated: boolean }> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<{ pathId: string; nodeId: string }, { regenerated: boolean }>(
    functions, 'regenerateAdjustedContent',
  );
  const result = await fn({ pathId, nodeId });
  return result.data;
}
